import { createSlice } from "@reduxjs/toolkit"
import types from "../../Actions/types"

/*
  We've used this file for several reducers, it's grown into more than just user-based reducers and should be broken up some
  time in the same way the rest of the Reducer is.
*/

// analytics
const analyticsInitial = {
  fetchingAnalytics: false,
  analyticsData: {},
  startDate: null,
  endDate: null,
  buttonAnalytics: [],
  videoAnalytics: []
}
const analyticsSlice = createSlice({
  name: "analytics",
  initialState: analyticsInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.UPDATE_ANALYTICS_DATES, (state, action) => {
      state.startDate = action.data.startDate
      state.endDate = action.data.endDate
    })
    builder.addCase(types.FETCHING_ANALYTICS, (state) => {
      state.fetchingAnalytics = true
    })
    builder.addCase(types.FETCHED_ANALYTICS, (state, action) => {
      state.fetchingAnalytics = false
      state.analyticsData = action.data.analyticsData
      state.videoCategoryAnalytics = action.data.videoData.categoryBreakdown
      state.videoTypeAnalytics = action.data.videoData.typeBreakdown
    })
    builder.addCase(types.FETCHED_BUTTON_ANALYTICS, (state, action) => {
      state.buttonAnalytics = action.data.analyticsData
    })
    builder.addCase(types.FETCHED_VIDEO_ANALYTICS, (state, action) => {
      state.videoAnalytics = action.data.analyticsData.videos
      state.videoCategoryAnalytics = action.data.analyticsData.categoryBreakdown
      state.videoTypeAnalytics = action.data.analyticsData.typeBreakdown
    })
  }
})

// network filters
const networkInitial = {
  search_text: "",
  search_item: "name",
  filter_state: "",
  filter_interests: "",
  filter_branch: ""
}
const networkSlice = createSlice({
  name: "network",
  initialState: networkInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.UPDATE_SEARCH_PARAMS, (state, action) => {
      state.search_text = action.data.search_text || ""
      state.search_item = action.data.search_item || "name"
      state.filter_state = action.data.filter_state || ""
      state.filter_interests = action.data.filter_interests || ""
      state.filter_branch = action.data.filter_branch || ""
    })
    builder.addCase(types.RESET_SEARCH_PARAMS, (state) => {
      state.search_text = ""
      state.search_item = "name"
      state.filter_state = ""
      state.filter_interests = ""
      state.filter_branch = ""
    })
  }
})

// network users (search results)
const networkUsersInitial = {
  users: [],
  page: 1,
  total: 10,
  lastPage: 10,
  fetchingSearchUsers: false
}
const networkUsersSlice = createSlice({
  name: "networkUsers",
  initialState: networkUsersInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.SET_SEARCH_USERS, (state, action) => {
      state.users = action.data.users
      state.total = action.data.total
      state.page = action.data.page
      state.lastPage = action.data.lastPage
    })
    builder.addCase(types.ADD_SEARCH_USERS, (state, action) => {
      state.users =
        state.users.length <= action.data.limit * action.data.page
          ? state.users
              .slice(0, action.data.limit * action.data.page - 1 * action.data.limit)
              .concat(action.data.users)
          : state.users
              .slice(0, action.data.limit * action.data.page - 1 * action.data.limit)
              .concat(action.data.users)
              .concat(state.users.slice(action.data.limit) * action.data.page)
      state.total = action.data.total
      state.page = action.data.page
      state.lastPage = action.data.lastPage
    })
    builder.addCase(types.FETCHING_SEARCH_USERS, (state, action) => {
      state.fetchingSearchUsers = action.data.toggle
    })
  }
})

// api tokens
const apiInitial = {
  apiToken: "",
  refreshToken: "",
  refreshingToken: false
}
const apiSlice = createSlice({
  name: "api",
  initialState: apiInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.SET_API_KEY, (state, action) => {
      state.apiToken = action.apiToken ? `Bearer ${action.apiToken}` : ""
      state.refreshToken = action.refreshToken || ""
    })
    builder.addCase(types.CLEAR_API_KEY, (state) => {
      state.apiToken = ""
      state.refreshToken = ""
      state.refreshingToken = false
    })
    builder.addCase(types.REFRESHING_TOKEN, (state) => {
      state.refreshingToken = true
    })
    builder.addCase(types.REFRESHED_TOKEN, (state, action) => {
      state.apiToken = `Bearer ${action.data.token}`
      state.refreshToken = action.data.refreshToken
      state.refreshingToken = false
    })
  }
})

// instructional guide
const guideInitial = {
  guide: [
    { id: 0, name: "navigation" },
    { id: 1, name: "dots" },
    { id: 2, name: "saveAndExit" }
  ],
  currentStepId: 0
}
const instructionalGuideSlice = createSlice({
  name: "instructionalGuide",
  initialState: guideInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.LOGOUT, (state) => {
      state.currentStepId = 0
    })
    builder.addCase(types.LOGIN, (state) => {
      state.currentStepId = 0
    })
    builder.addCase(types.INCREMENT_INSTRUCTION_STEP, (state) => {
      const incrementedStep = state.currentStepId + 1
      state.currentStepId =
        state.currentStepId + 1 < state.guide.length ? incrementedStep : -1
    })
  }
})

// user
const userInitial = {
  user: {},
  creatingAccount: false,
  fetchingUser: false,
  intervals: {},
  redirectUrl: "",
  loggingIn: false,
  isUpdatingProfileThumbnail: false,
  hasCheckedProfile: false
}
const userSlice = createSlice({
  name: "user",
  initialState: userInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.LOGGING_IN, (state) => {
      state.loggingIn = true
    })
    builder.addCase(types.SET_LOGIN_REDIRECT, (state, action) => {
      state.redirectUrl = action.data.url
    })
    builder.addCase(types.UPDATE_PATH_HISTORY, (state, action) => {
      state.pathHistory = action.data
    })
    builder.addCase(types.ADD_TO_RECENTLY_WATCHED, (state, action) => {
      state.user = { ...state.user, recentlyWatched: action.data }
    })
    builder.addCase(types.CREATING_ACCOUNT, (state) => {
      state.creatingAccount = true
    })
    builder.addCase(types.LOGOUT, (state) => {
      state.user = {}
    })
    builder.addCase(types.ADD_INTERVAL, (state, action) => {
      state.intervals = { ...state.intervals, ...action.data }
    })
    builder.addCase(types.FETCHING_USER, (state) => {
      state.fetchingUser = true
    })
    builder.addCase(types.FETCHED_USER, (state, action) => {
      state.user = { ...state.user, ...action.user }
      state.fetchingUser = false
    })
    builder.addCase(types.LOGIN, (state, action) => {
      state.creatingAccount = false
      state.redirectUrl = ""
      state.loggingIn = false
      state.user = { ...action.user }
    })
    builder.addCase(types.UPDATING_PROFILE_THUMBNAIL, (state) => {
      state.isUpdatingProfileThumbnail = true
    })
    builder.addCase(types.UPDATED_PROFILE_THUMBNAIL, (state) => {
      state.isUpdatingProfileThumbnail = false
    })
    builder.addCase(types.TOGGLE_HAS_CHECKED_PROFILE, (state, action) => {
      state.hasCheckedProfile = action.data
    })
    builder.addCase(types.SET_USER_INPUT_DATA, (state, action) => {
      // Update user data based on input data
      action.data.forEach(input => {
        if (input.userField && input.value !== undefined && input.value !== null) {
          const fieldPath = input.userField.split('.')
          if (fieldPath.length === 2) {
            if (fieldPath[0] === 'biography') {
              if (!state.user.biography) {
                state.user.biography = {}
              }
              state.user.biography[fieldPath[1]] = input.value
            }
          }
        }
      })
    })
    builder.addDefaultCase((state) => {
      state.creatingAccount = false
    })
  }
})

// modal
const modalInitial = {
  modal: {
    cropProfile: false,
    videoUpload: false,
    coachesNotes: false,
    apiTimeout: false,
    programDisabled: false,
    simpleModal: false
  },
  image_for_cropping: null,
  text: ""
}
const modalSlice = createSlice({
  name: "modal",
  initialState: modalInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.TOGGLE_MODAL_CROP, (state, action) => {
      Object.assign(state, action.data)
      state.modal = { ...state.modal, ...action.data.modal }
    })
    builder.addCase(types.TOGGLE_MODAL, (state, action) => {
      const { modal: modalData, title, text } = action.data
      state.modal = { ...modalData }
      state.title = title
      state.text = text
    })
  }
})

// errors
const errorsInitial = {
  exception: null,
  message: null,
  hasError: false,
  openErrorDialog: false,
  rollbar: null
}
const errorsSlice = createSlice({
  name: "errors",
  initialState: errorsInitial,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.EXCEPTION, (state, action) => {
      state.exception = action.data
    })
    builder.addCase(types.SETUP_ROLLBAR, (state, action) => {
      state.rollbar = action.data.rollbar
    })
    builder.addCase(types.LOGIN, (state) => {
      state.exception = null
      state.message = null
      state.errorData = null
      state.hasError = false
      state.openErrorDialog = false
    })
    builder.addCase(types.RESET_ERRORS, (state) => {
      state.hasError = false
      state.errorData = null
      state.exception = null
      state.message = null
      state.openErrorDialog = false
    })
    builder.addCase(types.OPEN_ERROR_MODAL, (state, action) => {
      state.openErrorDialog = true
      state.message = action.data.message.toString()
    })
    builder.addCase(types.ERROR, (state, action) => {
      if (
        (action.message && `${action.message}`.includes("Error: timeout")) ||
        (action.data && `${action.data}`.includes("Error: timeout"))
      ) {
        state.openErrorDialog = true
      } else {
        state.hasError = true
        state.errorData = action.data
      }
    })
    builder.addDefaultCase((state) => {
      state.message = null
      state.openErrorDialog = false
    })
  }
})

// Keep the exported shape the same so combineReducers spread still works
export default {
  errors: errorsSlice.reducer,
  user: userSlice.reducer,
  api: apiSlice.reducer,
  instructionalGuide: instructionalGuideSlice.reducer,
  modal: modalSlice.reducer,
  analytics: analyticsSlice.reducer,
  network: networkSlice.reducer,
  networkUsers: networkUsersSlice.reducer
}
